import React from 'react';
import { ArrowDropDown } from '@mui/icons-material';
import { BoxProps, FormControl, FormHelperText, InputLabel, MenuItem, Select, Tooltip } from '@mui/material';
import { themeColors } from '/src/theme';

export type FuseSelectProps = {
  /**
   * Select label
   */
  label?: string;
  /**
   * Select options
   */
  options: string[] | { label: string; value: string }[];
  /**
   * Sets the current value
   * @param value
   * @returns void
   */
  updateValue: (value: any) => void;
  /**
   * Select value
   */
  value?: any;
  /**
   * Data field associated with this input
   */
  field?: string;
  /**
   * The enclosing form's values
   */
  formValues?: any;
  /**
   * Text to display if no value is set
   */
  placeholder?: string;
  /**
   * Element flex amount
   */
  flex?: string;
  /**
   * If true, show an error is value is empty
   */
  required?: boolean;
  /**
   * Tooltip to display on hover
   */
  tooltip?: string;
  /**
   * Icon to display
   */
  icon?: React.ReactNode;
  /**
   * Element ID
   */
  id?: string;
  /**
   * Whether this component is disabled
   */
  disabled?: boolean;
  /**
   * Element class
   */
  className?: string;
  /**
   * Props to pass into Box component
   */
  boxProps?: BoxProps;
  /**
   * Text color
   */
  color?: string;
  /**
   * Form error
   */
  error?: string;
  /**
   * Screen reader label
   */
  ariaLabel?: string;
  /**
   * Shows the Select label
   */
  showLabel?: boolean;
  /**
   * Makes the placeholder value a selectable option
   */
  showPlaceholderOption?: boolean;
  /**
   * Enables a selectable placeholder value
   */
  enablePlaceholderOption?: boolean;
  /**
   * Disables the component's bottom border
   */
  disableUnderline?: boolean;
  /**
   * Disables this component's margin
   */
  disableMargin?: boolean;
  /**
   * Whether the component takes up the full with of the page
   */
  fullWidth?: boolean;
};

export const FuseSelect: React.FC<FuseSelectProps> = ({
  label,
  options,
  updateValue,
  value: _value,
  field,
  formValues,
  placeholder,
  required,
  tooltip,
  id,
  disabled,
  className,
  color,
  error,
  ariaLabel,
  showLabel = false,
  showPlaceholderOption = true,
  enablePlaceholderOption = false,
  disableUnderline = false,
  disableMargin = false,
  fullWidth = true,
}) => {
  const value = _value ?? formValues?.[field ?? ''] ?? '';
  const placeholderText = placeholder;
  const ariaText = ariaLabel || placeholderText;

  const labelId = id || `${label?.toLocaleLowerCase()}-select-label`;
  const labelEl = label ? (
    <InputLabel htmlFor={id} shrink={!!placeholderText} id={labelId}>
      {label}
    </InputLabel>
  ) : undefined;

  const ArrowDropDownIcon = () => <ArrowDropDown sx={{ color }} />;

  return (
    <FormControl
      fullWidth
      variant="standard"
      error={!!error}
      sx={!disableMargin ? { margin: '20px 0 26px 0' } : undefined}
    >
      {showLabel && labelEl ? tooltip ? <Tooltip title={tooltip}>{labelEl}</Tooltip> : labelEl : labelEl}
      <Select
        fullWidth={fullWidth}
        id={id}
        labelId={labelId}
        value={value}
        aria-placeholder={ariaText}
        aria-label={ariaText}
        onChange={(e) => updateValue(e.target.value)}
        required={required}
        disabled={disabled}
        className={className}
        disableUnderline={disableUnderline}
        displayEmpty
        // color="rgb(255,255,255)"
        // sx={{ color: color }}
        sx={{ color: color || (showPlaceholderOption && !value) ? color || themeColors.placeholderText : color }}
        IconComponent={ArrowDropDownIcon}
      >
        {showPlaceholderOption && (
          <MenuItem disabled={!enablePlaceholderOption} value="">
            {placeholderText}
          </MenuItem>
        )}
        {options.map((option) => {
          const optionValue = typeof option === 'string' ? option : option.value;
          const optionLabel = typeof option === 'string' ? option : option.label;

          return (
            <MenuItem key={optionValue} value={optionValue} selected={value === optionValue}>
              {optionLabel}
            </MenuItem>
          );
        })}
      </Select>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};
